import React, { useState, useEffect } from "react";
import { Button } from "./ui";
import { useLocation, useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBCardTitle, MDBTable, MDBTableHead, MDBTableBody, MDBIcon, MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import NativeInput from "./NativeInput";
import CustomDropdown from "./dropdown/CustomDropdown";
import { api } from "../services";
import {
  fetchTrackHubBranches,
  fetchTrackHubCarriers,
  patchTrackHubBranch,
} from "../services/trackHubApi";
import { toast } from "react-toastify";
import { Files, Check } from "lucide-react";
import Actionbar from "./Actionbar";
import { useTranslation } from "react-i18next";
import {
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import IntegrationMoreModal from "./Modals/IntegrationMoreModal";
import ConfirmModal from "./Modals/ConfirmModal";
import AvailabilityScheduleCard from "./AvailabilityScheduleCard";
import EditAvailabilityDrawer from "./EditAvailabilityDrawer";
import "./view-branch.css";
import "./trackhub/trackhub-branches.css";
import "./trackhub/trackhub-branch-modal.css";
import {
  TrackHubActivateSelect,
  TrackHubShipmentSelect,
} from "./trackhub/TrackHubBranchSelects";
import { usePermission } from "../hooks/usePermission";
import {
  RBAC_MODULES,
  PERMISSION_ACTIONS,
} from "../config/routePermissions";
import { resolveTenantApiError } from "../utils/resolveTenantApiError";
import {
  orderAgentLogoEntries,
  isOrderAgentPlatform,
  isOrderAgentApp,
  resolveIntegrationLogo,
  LOOPS_LOGO_SRC,
  usesWideIntegrationLogo,
} from "../utils/platformLogos";
import { getLocalizedPlatformName } from "../utils/platformNames";
import {
  formatLocalizedDateTimeParts,
  resolveDateLocale,
} from "../utils/formatDateTime";
import {
  isBranchFoodicsIntegrated,
  isBranchTabSenseIntegrated,
  isBranchPosAutoAcceptLocked,
  isBranchPartnerManaged,
  branchPartnerAutoAcceptTooltipKey,
  branchPosConflictTooltipKey,
} from "../utils/posReadOnly";

/** Synthetic Activate option — not an AvailableApp; persisted as allowManualShipment. */
const MANUAL_SHIPMENT_TYPE = "MANUAL_SHIPMENT";

const DAYS_OF_WEEK = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function isValidTime(time) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
}

function isStartBeforeEnd(start, end) {
  if (!isValidTime(start) || !isValidTime(end)) return false;
  return start < end;
}

const platformLogos = {
  JAHEZ: {
    src: "/images/jahez-new-logo.svg",
    alt: "Jahez",
  },
  "JAHEZ - STAGING": {
    src: "/images/jahez-new-logo.svg",
    alt: "Jahez",
  },
  KEETA: {
    src: "/images/keeta.svg",
    alt: "Keeta",
  },
  "THE CHEFZ": {
    src: "/images/the_chefz_logo.svg",
    alt: "The Chefz",
  },
  "THE CHEFZ STAGING": {
    src: "/images/the_chefz_logo.svg",
    alt: "The Chefz",
  },
  NINJA: {
    src: "/images/ninga.svg",
    alt: "Ninja",
  },
  "NINJA - STAGING": {
    src: "/images/ninga.svg",
    alt: "Ninja",
  },
  FOODICS: {
    src: "/images/foodics.svg",
    alt: "Foodics",
  },
  TABSENSE: {
    src: "/images/tab_sense_colred.svg",
    alt: "TabSense",
  },
  ...orderAgentLogoEntries(),
  DIGITAL_CATALOG: {
    src: "/images/loops.svg",
    alt: "Digital Catalog",
  },
  "DIGITAL CATALOG": {
    src: "/images/loops.svg",
    alt: "Digital Catalog",
  },
};

const getAppLogo = (appName) => {
  if (!appName) return "";
  const upperName = appName.toUpperCase();
  // Check for direct match first
  if (platformLogos[upperName]) {
    return platformLogos[upperName].src;
  }

  // Fallback for partial matches if needed, though exact keys above handle the requested cases
  if (upperName.includes("NINJA")) return platformLogos["NINJA"].src;
  if (upperName.includes("JAHEZ")) return platformLogos["JAHEZ"].src;
  if (upperName.includes("CHEFZ")) return platformLogos["THE CHEFZ"].src;
  if (upperName.includes("TABSENSE")) return platformLogos["TABSENSE"].src;
  if (upperName.includes("DIGITAL")) return platformLogos["DIGITAL CATALOG"].src;
  if (isOrderAgentPlatform(appName)) return platformLogos.CHAT_AGENT.src;

  return "";
};

const getBranchDisplayName = (branch, isArabic) => {
  if (!branch) return "";
  if (isArabic) {
    const arName = String(branch?.name?.ar || branch?.name_ar || "").trim();
    return arName || "NA";
  }
  return branch?.name?.en || branch?.name || "";
};

const ViewBranchPageV1 = () => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const { can } = usePermission();
  const canManageAutoAccessOrders = can(
    RBAC_MODULES.SETTINGS,
    PERMISSION_ACTIONS.MANAGE_AUTO_ACCESS_ORDERS
  );
  const canManageBranchMappings = can(
    RBAC_MODULES.SETTINGS,
    PERMISSION_ACTIONS.MANAGE_BRANCH_MAPPINGS
  );
  const canAddIntegrations = can(
    RBAC_MODULES.APP_MANAGEMENT,
    PERMISSION_ACTIONS.ADD_INTEGRATIONS
  );
  const canUpdateBranchAppStatus =
    canManageBranchMappings || canAddIntegrations;
  const canManageAddonSettings = can(
    RBAC_MODULES.SETTINGS,
    PERMISSION_ACTIONS.MANAGE_ADDON_SETTINGS
  );
  const noPermissionTip = t("noPermissionToAccessThis");
  const addonNotEnabledTip = t(
    "Please contact the support team to enable this feature for your account."
  );
  const autoAcceptOrdersPermissionTooltip = t("autoAcceptOrdersPermissionTooltip");
  const { state } = useLocation();
  const navigate = useNavigate();
  const { storeId, branchId } = state || {};
  const [branch, setBranch] = useState(null);
  console.log("🚀 ~ ViewBranchPageV1 ~ branch:", branch)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [tempBranch, setTempBranch] = useState(null);
  const [addingSlot, setAddingSlot] = useState(null); // Track which slot type is being added
  const [apps, setApps] = useState([]);
  console.log("🚀 ~ ViewBranchPageV1 ~ apps:", apps)
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  
  // Add-Ons state
  const [addOnApps, setAddOnApps] = useState([]);
  console.log("🚀 ~ ViewBranchPageV1 ~ addOnApps:", addOnApps)
  const [addOnCatalogs, setAddOnCatalogs] = useState([]);
  //console.log("🚀 ~ ViewBranchPageV1 ~ addOnCatalogs:", addOnCatalogs)
  const [chatAgentData, setChatAgentData] = useState(null);
  const [digitalCatalogData, setDigitalCatalogData] = useState(null);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [updatingCatalog, setUpdatingCatalog] = useState({});
  const [updatingSlug, setUpdatingSlug] = useState(false);
  const [slugValue, setSlugValue] = useState("");
  const [slugError, setSlugError] = useState("");
  const [slugValid, setSlugValid] = useState(false);
  const [updatingAutoAccept, setUpdatingAutoAccept] = useState(false);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [activeAddOn, setActiveAddOn] = useState(null);
  //console.log("🚀 ~ ViewBranchPageV1 ~ activeAddOn:", activeAddOn)
  const [addOnStatuses, setAddOnStatuses] = useState({}); // Store status for each add-on by addOnAppId
  const [branchRegister, setNewBranchRegistered] = useState(null);
  
  // Branch Status Modal states
  const [showBranchStatusModal, setShowBranchStatusModal] = useState(false);
  const [branchStatusData, setBranchStatusData] = useState(null);
  const [trackHubModalConfig, setTrackHubModalConfig] = useState(null);
  const [trackHubAccountCarriers, setTrackHubAccountCarriers] = useState([]);
  const [savingTrackHubModal, setSavingTrackHubModal] = useState(false);
  const [loadingBranchStatus, setLoadingBranchStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [slotErrors, setSlotErrors] = useState({}); // { fieldName_index: { day: '', start: '', end: '' } }
  const [keetaShops, setKeetaShops] = useState([]);
  const [selectedKeetaShopId, setSelectedKeetaShopId] = useState("");
  const [keetaShopDetails, setKeetaShopDetails] = useState(null);
  const [keetaAvailable, setKeetaAvailable] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showEditAvailabilityModal, setShowEditAvailabilityModal] =
    useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showAutoAcceptConfirm, setShowAutoAcceptConfirm] = useState(false);
  const [autoAcceptModalData, setAutoAcceptModalData] = useState({
    title: "",
    message: "",
    action: () => {},
  });
  const [updatingEnableIntegration, setUpdatingEnableIntegration] = useState(false);
  // Inside your Parent Component function
const [showMoreModal, setShowMoreModal] = useState(false);
const [activeApp, setActiveApp] = useState(null);
const [activeSync, setActiveSync] = useState(null);
const handleOpenMore = (app, latestSync) => {
  setActiveApp(app);
  setActiveSync(latestSync);
  setShowMoreModal(true);
};
  const [togglingSync, setTogglingSync] = useState({});

  const [filteredKeetaShops, setFilteredKeetaShops] = useState([]);
  console.log("filterKeetaShops", filteredKeetaShops);

  const keetaSync = Array.isArray(branch?.syncedApps)
    ? branch.syncedApps
        .filter((app) => app.app === "Keeta")
        .sort((a, b) => new Date(b.synced_date) - new Date(a.synced_date))[0]
    : null;

  const keetaExternalId = keetaSync?.external_id;

  const fetchKeetaAppDetails = async () => {
    try {
      const response = await api.get(`/keeta-shops/${keetaExternalId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setKeetaShopDetails(response.data.data);
    } catch (error) {
      console.error("Error fetching keeta shop details:", error);
      // Clear keeta shop details if fetch fails
      setKeetaShopDetails(null);
    }
  };
  const fetchStoreDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data?.branches || []);
    } catch (error) {
      console.error(
        "Error fetching store details:",
        error.response?.data || error.message
      );
    }
  };

  useEffect(() => {
    if (keetaExternalId) {
      fetchKeetaAppDetails();
    } else {
      // Clear keeta shop details if no external ID
      setKeetaShopDetails(null);
    }
  }, [keetaExternalId]);

  useEffect(() => {
    const fetchStoreApps = async () => {
      if (!storeId) return;

      try {
        const token = localStorage.getItem("token");

        const response = await api.get(`/stores/${storeId}/apps`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const apps = response.data?.apps || [];

        const keetaExists = apps.some(
          (app) => app.application?.toLowerCase() === "keeta"
        );

        setKeetaAvailable(keetaExists);
      } catch (err) {
        console.error("Error fetching store apps:", err);
      }
    };

    fetchStoreApps();
  }, [storeId]);

  const getLatestSuccessfulSync = (branch) => {
    const syncedApps = branch.syncedApps || [];
    console.log("🚀 ~ getLatestSuccessfulSync ~ syncedApps:", syncedApps);

    const findAppForSync = (syncAppName) =>
      apps.find(
        (a) =>
          a.application &&
          syncAppName &&
          a.application.toLowerCase() === syncAppName.toLowerCase()
      );

    // Match catalog apps case-insensitively (e.g. TabSense vs tabsense) — same idea as getLatestSyncForApp
    const successfulSyncs = syncedApps.filter((sync) => !!findAppForSync(sync.app));

    const syncsByApp = successfulSyncs.reduce((acc, sync) => {
      const matchedApp = findAppForSync(sync.app);
      if (!matchedApp?.application) return acc;
      const key = matchedApp.application;

      if (
        !acc[key] ||
        new Date(sync.synced_date) > new Date(acc[key].synced_date)
      ) {
        acc[key] = {
          ...sync,
          app: key,
          isIntegrated: true,
          appDetails: matchedApp,
        };
      }
      return acc;
    }, {});

    return Object.values(syncsByApp).sort((a, b) => {
      // Integrated apps first
      if (a.isIntegrated && !b.isIntegrated) return -1;
      if (!a.isIntegrated && b.isIntegrated) return 1;
      // Then by name
      return a.app.localeCompare(b.app);
    });
  };

  const handleOpenEditAvailability = () => {
    setShowEditAvailabilityModal(true);
  };

  const handleSaveAvailability = (updatedAvailability) => {
    setTempBranch((prev) => ({
      ...prev,
      availableHours: updatedAvailability,
    }));
    setShowEditAvailabilityModal(false);
  };

  const handleCloseAvailabilityModal = () => {
    setShowEditAvailabilityModal(false);
  };

  // Add this fetch function
  const fetchAllKeetaShops = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/keeta-shops/store/${storeId}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeetaShops(response.data.data?.shops || []);
    } catch (error) {
      console.error("Error fetching keetaShops:", error);
      setKeetaShops([]);
    }
  };

  // Add this useEffect to fetch keeta shops when component mounts
  useEffect(() => {
    fetchAllKeetaShops();
    fetchStoreDetails();
  }, []);

  useEffect(() => {
    if (
      keetaShopDetails &&
      keetaShopDetails.keetaShopId &&
      keetaShops.length > 0
    ) {
      // Find the shop that matches either by keetaShopId or _id
      const matchingShop = keetaShops.find(
        (shop) =>
          shop.keetaShopId === keetaShopDetails.keetaShopId ||
          shop._id === keetaExternalId
      );

      if (matchingShop) {
        setSelectedKeetaShopId(matchingShop._id);
      }
    } else {
      // Clear selected shop if no keeta shop details
      setSelectedKeetaShopId("");
    }
  }, [keetaShopDetails, keetaShops, keetaExternalId]);
  const filterKeetaShopsLinkedToOtherBranches = () => {
    const currentBranch = branches.find(
      (branch) => String(branch._id) === String(branchId)
    );
    const currentBranchKeetaShopIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (app.app?.toLowerCase().includes("keeta") && app.external_id) {
          const shop = keetaShops.find(
            (s) => String(s.keetaShopId) === String(app.external_id)
          );
          if (shop) {
            currentBranchKeetaShopIds.add(String(shop._id));
          }
        }
      });
    }

    const allLinkedKeetaShopIds = new Set();

    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (app.app?.toLowerCase().includes("keeta") && app.external_id) {
          const shop = keetaShops.find(
            (s) => String(s.keetaShopId) === String(app.external_id)
          );
          if (shop) {
            allLinkedKeetaShopIds.add(String(shop._id));
          }
        }
      });
    });

    const result = keetaShops.filter((shop) => {
      const shopDocId = String(shop._id);
      return (
        !allLinkedKeetaShopIds.has(shopDocId) ||
        currentBranchKeetaShopIds.has(shopDocId)
      );
    });

    setFilteredKeetaShops(result);
  };

  useEffect(() => {
    if (keetaShops.length && branches.length && branchId) {
      filterKeetaShopsLinkedToOtherBranches();
    }
  }, [keetaShops, branches, branchId]);

  // Add this function to filter keeta shops based on selected app

  const MANDATORY_FIELDS = [
    "name",
    "name_ar",
    "email",
    "phone",
    "city",
    "address",
    "latitude",
    "longitude",
  ];

  const initialFieldErrors = {
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
    name_ar: "",
  };

  const validateField = (fieldName, value) => {
    let errorMessage = "";
    switch (fieldName) {
      case "name":
        if (!value || !value.trim()) {
          errorMessage = `${t("Branch name is required")}`;
        } else if (value.trim().length > 23) {
          errorMessage = `${t("Branch name must not exceed 23 characters")}`;
        }
        break;
      case "name_ar":
        if (!value || !value.trim()) {
          errorMessage = `${t("Arabic branch name is required")}`;
        } else {
          const arabicRegex = /^[\u0600-\u06FF0-9 ]+$/;
          if (!arabicRegex.test(value.trim())) {
            errorMessage = `${t(
              "Only Arabic letters, numbers, and spaces are allowed"
            )}`;
          } else if (value.trim().length > 23) {
            errorMessage = `${t("Arabic branch name must not exceed 23 characters")}`;
          }
        }
        break;
      case "email":
        if (!value || !value.trim()) {
          errorMessage = `${t("Email is required")}`;
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errorMessage = `${t("Please enter a valid email address")}`;
        }
        break;
      case "phone":
        if (!value || !value.trim()) {
          errorMessage = `${t("Phone number is required")}`;
        } else {
          const cleanPhone = value.replace(/\D/g, "");
          if (
            (cleanPhone.length === 13 && cleanPhone.startsWith("966")) ||
            (cleanPhone.length === 12 && cleanPhone.startsWith("966")) ||
            (cleanPhone.length === 10 && cleanPhone.startsWith("05")) ||
            (cleanPhone.length === 9 && cleanPhone.startsWith("5"))
          ) {
            // Valid KSA format
          } else {
            errorMessage = `${t(
              "Please enter a valid KSA phone number (e.g., +966501234567, 966501234567, 0501234567, or 501234567)"
            )}`;
          }
        }
        break;
      case "city":
        if (!value || !value.trim()) {
          errorMessage = `${t("City is required")}`;
        }
        break;
      case "address":
        if (!value || !value.trim()) {
          errorMessage = `${t("Address is required")}`;
        }
        break;
      case "latitude":
        if (!value || !value.trim()) {
          errorMessage = `${t("Latitude is required")}`;
        } else if (isNaN(value) || value < -90 || value > 90) {
          errorMessage = `${t("Please enter a valid latitude (-90 to 90)")}`;
        }
        break;
      case "longitude":
        if (!value || !value.trim()) {
          errorMessage = `${t("Longitude is required")}`;
        } else if (isNaN(value) || value < -180 || value > 180) {
          errorMessage = `${t("Please enter a valid longitude (-180 to 180)")}`;
        }
        break;
      default:
        break;
    }
    return errorMessage;
  };

  const [fieldErrors, setFieldErrors] = useState(initialFieldErrors);

  const handleFieldChangeValidated = (field, value) => {
    setTempBranch((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
    setError("");
  };

  const validateAllFields = () => {
    let hasErrors = false;
    const newErrors = { ...initialFieldErrors };
    MANDATORY_FIELDS.forEach((field) => {
      const value = tempBranch[field];
      const errorMsg = validateField(field, value);
      newErrors[field] = errorMsg;
      if (errorMsg) hasErrors = true;
    });
    setFieldErrors(newErrors);
    return !hasErrors;
  };

  const fetchBranchDetails = async () => {
    try {
      if (!storeId || !branchId) {
        throw new Error("Missing store or branch ID");
      }

      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branch/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setBranch(data);
      setTempBranch(normalizeBranchForEdit(data));
    } catch (err) {
      setError(err.message || `${t("Failed to load branch details")}`);
      console.error("Error fetching branch:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/apps", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setApps(response.data.apps || []);
      } catch (err) {
        console.error("Error fetching apps:", err.message);
      }
    };

    const fetchAvailableAddOns = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/available-apps/categorized", {
          headers: { Authorization: `Bearer ${token}` },
          params: storeId ? { storeId } : undefined,
        });
        const addOns = response.data?.categories?.addOns?.apps || [];
        setAddOnApps(addOns);  // Set directly to addOnApps for rendering
        setAddOnCatalogs(addOns);  // Also keep in addOnCatalogs for reference
        console.log("Fetched Add-Ons for rendering:", addOns);
      } catch (err) {
        console.error("Error fetching add-ons:", err.message);
      }
    };

    const fetchCities = async () => {
      try {
        const token = localStorage.getItem("token");
        setLoadingCities(true);
        const response = await api.get("/cities", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCities(response.data.data);
        setLoadingCities(false);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(`${t("Failed to load cities. Please try again later.")}`);
        setLoadingCities(false);
      }
    };

    const fetchAllAddOnStatuses = async () => {
      // Fetch add-ons first to get their IDs
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/available-apps/categorized", {
          headers: { Authorization: `Bearer ${token}` },
          params: storeId ? { storeId } : undefined,
        });
        const addOns = response.data?.categories?.addOns?.apps || [];
        
        // Fetch status for each add-on
        const statusPromises = addOns.map(async (addOn) => {
          try {
            const statusResponse = await api.get("/add-ons/branches/status", {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                addOnAppId: addOn._id,
                limit: "1000",
                ...(storeId ? { storeId } : {}),
              },
            });
            const currentBranchData = statusResponse.data?.data?.find(
              (b) => b.branchId === branchId
            );
            return {
              addOnAppId: addOn._id,
              status: currentBranchData?.isConnected ? "Active" : "Inactive",
              isConnected: !!currentBranchData,
            };
          } catch (err) {
            console.error(`Error fetching status for ${addOn.name}:`, err);
            return { addOnAppId: addOn._id, status: "Inactive", isConnected: false };
          }
        });
        
        const statuses = await Promise.all(statusPromises);
        const statusMap = statuses.reduce((acc, item) => {
          acc[item.addOnAppId] = { status: item.status, isConnected: item.isConnected };
          return acc;
        }, {});

        const trackHubApp = addOns.find((addOn) => addOn.appType === "TRACK_HUB");
        if (trackHubApp && branchId) {
          try {
            const thResult = await fetchTrackHubBranches({ branchId, limit: 1 });
            const trackHubRow = thResult.data?.[0];
            if (trackHubRow) {
              statusMap[trackHubApp._id] = {
                status: trackHubRow.isEnabled ? "Active" : "Inactive",
                isConnected: !!trackHubRow.isEnabled,
              };
            }
          } catch (trackHubError) {
            console.error("Error fetching TrackHub branch status:", trackHubError);
          }
        }
        
        console.log("📊 Add-on statuses loaded:", statusMap);
        setAddOnStatuses(statusMap);
      } catch (err) {
        console.error("Error fetching add-on statuses:", err);
      }
    };

    fetchCities();

    fetchBranchDetails();
    fetchApps();
    fetchAvailableAddOns();
    fetchAllAddOnStatuses();
  }, [storeId, branchId, branchRegister]);

  // Get integrationId from branch.syncedApps when needed
  const getAddOnIntegrationData = (addOnAppId) => {
    if (!branch?.syncedApps) return null;
    
    const syncedApp = branch.syncedApps.find(
      (sync) => sync.appId === addOnAppId
    );
    
    return syncedApp ? {
      integrationId: syncedApp.integrationId,
      external_id: syncedApp.external_id,
      auto_order_accept: syncedApp.auto_order_accept || false,
    } : null;
  };

  const normalizeBranchForEdit = (data) => {
    if (!data) return data;
    const cloned = JSON.parse(JSON.stringify(data));
    const nameEn =
      typeof cloned.name === "object"
        ? cloned.name?.en || ""
        : cloned.name || "";
    const nameAr =
      cloned.name_ar ||
      (typeof cloned.name === "object" ? cloned.name?.ar : "") ||
      "";
    cloned.name = nameEn;
    cloned.name_ar = nameAr;
    return cloned;
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (editMode) {
      // Reset changes when canceling edit
      setTempBranch(normalizeBranchForEdit(branch));
      setAddingSlot(null); // Cancel any in-progress additions
      // Reset selected keeta shop to current state
      if (keetaShopDetails && keetaShops.length > 0) {
        const matchingShop = keetaShops.find(
          (shop) =>
            shop.keetaShopId === keetaShopDetails.keetaShopId ||
            shop._id === keetaExternalId
        );
        setSelectedKeetaShopId(matchingShop ? matchingShop._id : "");
      } else {
        setSelectedKeetaShopId("");
      }
      // Clear validation and status messages
      setFieldErrors(initialFieldErrors);
      setError("");
      setSuccessMessage("");
      setSlotErrors({});
    } else {
      setTempBranch(normalizeBranchForEdit(branch));
    }
  };

  const handleFieldChange = (field, value, index = null, subField = null) => {
    const updated = { ...tempBranch };

    if (index !== null) {
      // For array fields (availability, breakHours, holidayHours)
      if (subField) {
        updated[field][index][subField] = value;
      } else {
        updated[field][index] = value;
      }
    } else {
      // For direct fields
      updated[field] = value;
    }

    setTempBranch(updated);
  };

  const handleSaveChanges = async () => {
    if (!canManageBranchMappings) {
      toast.error(noPermissionTip);
      return;
    }
    if (!validateAllFields()) {
      setSuccessMessage("");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      console.log("🔄 Sending Data to Update Branch:", tempBranch);

      const payload = {
        ...tempBranch,
        name: {
          en: tempBranch?.name || "",
          ...(tempBranch?.name_ar ? { ar: tempBranch.name_ar } : {}),
        },
        // Send keetaShopId (could be empty string to clear the association)
        keetaShopId: selectedKeetaShopId || "",
      };

      const saveResponse = await api.put(`/stores/${storeId}/branch/${branchId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const saveWarnings = saveResponse.data?.warnings;
      if (Array.isArray(saveWarnings) && saveWarnings.length > 0) {
        saveWarnings.forEach((w) => {
          const m = w?.message || "";
          const tr = t(m);
          toast.warning(tr !== m ? tr : m);
        });
      }

      setBranch(tempBranch);
      setEditMode(false);
      setAddingSlot(null);
      toast.success(`${t("Branch updated successfully.")}`);

      // Refresh data after successful update
      await fetchBranchDetails();

      // If keetaShopId was cleared, also clear the local state
      if (!selectedKeetaShopId) {
        setKeetaShopDetails(null);
      } else {
        // Refresh keeta shop details if a shop was selected
        await fetchKeetaAppDetails();
      }

      setError("");
      setFieldErrors(initialFieldErrors);
    } catch (err) {
      console.log(err);
      const errors = err?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0] : null;

      toast.error(
        firstError ||
          resolveTenantApiError(err, t, "Failed to update branch")
      );
      // If server returns field-level errors, show them inline and stay in edit mode
      // if (err?.response?.data?.errors) {
      //   const apiErrors = err.response.data.errors;
      //   setFieldErrors((prev) => ({ ...prev, ...apiErrors }));
      //   setError("");
      //   setSuccessMessage("");
      //   // Stay in edit mode
      //   return;
      // }

      // For fatal errors, show the first available error or fallback

      setSuccessMessage("");
      console.error("Error updating branch:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = (slotType) => {
    setAddingSlot(slotType);
    // Add a new empty slot to the beginning of the array
    const updated = { ...tempBranch };
    updated[slotType] = [{ day: "", start: "", end: "" }, ...updated[slotType]];
    setTempBranch(updated);
  };

  const handleSaveNewSlot = (slotType) => {
    // Validation could be added here
    setAddingSlot(null);
  };

  const handleCancelAddSlot = (slotType) => {
    const updated = { ...tempBranch };
    // Remove the first item if it's empty (the one we were adding)
    if (
      updated[slotType][0] &&
      !updated[slotType][0].day &&
      !updated[slotType][0].start &&
      !updated[slotType][0].end
    ) {
      updated[slotType] = updated[slotType].slice(1);
    }
    setTempBranch(updated);
    setAddingSlot(null);
  };

  const handleDeleteSlot = (slotType, index) => {
    const updated = { ...tempBranch };
    updated[slotType] = updated[slotType].filter((_, i) => i !== index);
    setTempBranch(updated);
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    return time.includes(":") ? time : `${time.slice(0, 2)}:${time.slice(2)}`;
  };

  const handleIntegrationClick = async (app) => {
    if (!canManageBranchMappings) {
      toast.error(noPermissionTip);
      return;
    }
    const payload = {
      appId: app._id,
      branchId,
    };

    try {
      const token = localStorage.getItem("token");

      console.log(
        "🔄 Sending Data to Register Branch to third party app:",
        payload
      );

      const response = await api.post("/register-branch", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Branch Registered Successfully:", response.data);
      toast.success(`${t("Branch Register successfully.")}`);
      // Optionally handle success UI or state update here
      setNewBranchRegistered(response.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || `${t("Error registering branch.")}`
      );
      console.error(
        "❌ Error Registering Branch:",
        error.response?.data || error.message
      );
    }
  };

  const handleRemoveIntegration = async (app) => {};

  const validateSlug = (slug) => {
    if (!slug) return false;
    const slugRegex = /^[a-z0-9-]+$/;
    return slugRegex.test(slug);
  };

  const handleAddOnCatalogUpdate = async (addonType, catalogObj) => {
    if (!canManageAddonSettings) {
      toast.error(noPermissionTip);
      return;
    }
    if (!branchStatusData) return;

    setUpdatingCatalog({ ...updatingCatalog, [addonType]: true });

    try {
      const token = localStorage.getItem("token");
      
      const payload = {
        addOnAppId: branchStatusData.addOnAppId,
        branchId: branchId,
        catalogId: catalogObj?._id || null,
        enabled: catalogObj?._id ? (branchStatusData.isConnected || false) : false,
      };

      console.log("📤 Sending catalog update payload:", payload);

      const response = await api.post("/add-ons/branches/toggle", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t("Catalog updated successfully"));
      
      // Update local state
      setActiveAddOn({
        ...activeAddOn,
        catalog_id: catalogObj?._id,
      });
      
      // Refresh branch status data
      await fetchBranchStatus(branchStatusData.addOnAppId);
    } catch (error) {
      console.error("❌ Error updating catalog:", error);
      toast.error(
        error.response?.data?.message || t("Failed to update catalog")
      );
    } finally {
      setUpdatingCatalog({ ...updatingCatalog, [addonType]: false });
    }
  };

  const handleSlugUpdate = async () => {
    if (!canManageAddonSettings) {
      toast.error(noPermissionTip);
      return;
    }
    if (!slugValue || !validateSlug(slugValue)) {
      setSlugError(t("Invalid slug format. Use only lowercase letters, numbers, and hyphens."));
      return;
    }

    if (!branchStatusData) return;

    setUpdatingSlug(true);

    try {
      const token = localStorage.getItem("token");
      
      // Get catalog ID from activeAddOn or branchStatusData
      const catalogId = activeAddOn?.catalog_id || branchStatusData.integratedCatalogId || null;
      
      const payload = {
        addOnAppId: branchStatusData.addOnAppId,
        branchId: branchId,
        catalogId: catalogId,
        enabled: branchStatusData.isConnected || false,
        slug: slugValue,
      };

      console.log("📤 Sending slug update payload:", payload);

      const response = await api.post("/add-ons/branches/toggle", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t("Slug updated successfully"));
      
      // Update local state
      setBranchStatusData({
        ...branchStatusData,
        slug: slugValue,
      });
      
      setSlugError("");
    } catch (error) {
      console.error("❌ Error updating slug:", error);
      toast.error(
        error.response?.data?.message || t("Failed to update slug")
      );
    } finally {
      setUpdatingSlug(false);
    }
  };

  const handleAutoOrderAccept = async (autoAccept) => {
    if (!canManageAutoAccessOrders) {
      toast.error(noPermissionTip);
      return;
    }
    if (!activeAddOn) return;

    console.log("🔍 activeAddOn data:", activeAddOn);

    if (!activeAddOn.integrationId) {
      toast.error(t("Integration ID is missing. Please try again."));
      console.error("❌ Missing integrationId in activeAddOn:", activeAddOn);
      return;
    }

    setUpdatingAutoAccept(true);

    try {
      const token = localStorage.getItem("token");
      
      // Use the integrationId from activeAddOn (from syncedApps)
      const payload = {
        appIntegrationId: activeAddOn.integrationId,
        
        externalId: activeAddOn.external_id || branchId,
        autoOrderAccept: autoAccept,
      };
console.log("🚀 ~ handleAutoOrderAccept ~ activeAddOn.integrationId:", activeAddOn.integrationId)
      console.log("📤 Sending auto accept payload:", payload);

      const response = await api.post(
        `/branch/${branchId}/auto-order-status`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success(t("Auto accept order status updated"));
        
        // Update local state
        setActiveAddOn({
          ...activeAddOn,
          auto_accept: autoAccept,
        });
        
        // Optionally refresh branch details
        await fetchBranchDetails();
      } else {
        throw new Error(`Server responded with status ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error updating auto accept order status:", error);
      toast.error(
        `${t("Failed to update auto accept order status.")} ${t("Please try again later.")}`
      );
    } finally {
      setUpdatingAutoAccept(false);
    }
  };

  const handleAutoAcceptToggle = () => {
    const isEnabling = !activeAddOn.auto_accept;

    if (isEnabling) {
      // Show confirmation modal when enabling
      setAutoAcceptModalData({
        title: t("Confirm Auto-Accept Orders"),
        message: t(
          "You agree that you are allowing Loops to accept all orders continuously. If items are out of stock (OOS), you need to update the inventory."
        ),
        action: () => handleAutoOrderAccept(true),
      });
      setShowAutoAcceptConfirm(true);
    } else {
      // Directly disable without confirmation
      handleAutoOrderAccept(false);
    }
  };

  const handleEnableIntegration = async (enabled) => {
    if (!canManageAddonSettings) {
      toast.error(noPermissionTip);
      return;
    }
    if (!activeAddOn) return;

    console.log("🔍 activeAddOn data for enable integration:", activeAddOn);

    if (!activeAddOn.addOnAppId) {
      toast.error(t("Add-on ID is missing. Please try again."));
      console.error("❌ Missing addOnAppId in activeAddOn:", activeAddOn);
      return;
    }

    if (enabled && activeAddOn.addon_type !== "TRACK_HUB" && !activeAddOn.catalog_id) {
      toast.error(t("AddOnIntegration.selectCatalogTooltip"));
      return;
    }

    // Store previous state for rollback on error
    const previousStatus = addOnStatuses[activeAddOn.addOnAppId];
    const previousActiveAddOn = { ...activeAddOn };

    // Optimistic update - update UI immediately
    setAddOnStatuses(prev => ({
      ...prev,
      [activeAddOn.addOnAppId]: {
        status: enabled ? "Active" : "Inactive",
        isConnected: true,
      }
    }));
    
    setActiveAddOn(prev => ({
      ...prev,
      enabled: enabled,
      status: enabled ? "Active" : "Inactive",
    }));

    setUpdatingEnableIntegration(true);

    try {
      const token = localStorage.getItem("token");
      
      const payload = {
        addOnAppId: activeAddOn.addOnAppId,
        branchId: branchId,
        catalogId: activeAddOn.catalog_id,
        enabled: enabled,
      };

      if (activeAddOn.addon_type === "TRACK_HUB") {
        await api.post(`/add-ons/branches/toggle`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await patchTrackHubBranch(branchId, {
          enabled,
          orderSources:
            trackHubModalConfig?.orderSources ||
            trackHubModalConfig?.activatedAddons ||
            [],
        });
      } else {
        console.log("📤 Sending enable integration payload:", payload);

        const response = await api.post(
          `/add-ons/branches/toggle`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Server responded with status ${response.status}`);
        }
      }

      toast.success(t("Integration status updated successfully"));
      fetchBranchStatus(activeAddOn.addOnAppId);
      if (activeAddOn.addon_type === "TRACK_HUB") {
        const thResult = await fetchTrackHubBranches({ branchId, limit: 1 });
        setTrackHubModalConfig(thResult.data?.[0] || null);
      }
      fetchBranchDetails();
    } catch (error) {
      console.error("❌ Error updating integration status:", error);
      toast.error(
        `${t("Failed to update integration status.")} ${t("Please try again later.")}`
      );
      
      // Rollback optimistic update on error
      if (previousStatus) {
        setAddOnStatuses(prev => ({
          ...prev,
          [activeAddOn.addOnAppId]: previousStatus,
        }));
      }
      setActiveAddOn(previousActiveAddOn);
    } finally {
      setUpdatingEnableIntegration(false);
    }
  };

  const handleEnableIntegrationToggle = () => {
    const isEnabling = !activeAddOn.enabled;
    handleEnableIntegration(isEnabling);
  };

  const trackHubModalIsActive =
    activeAddOn?.addon_type === "TRACK_HUB"
      ? !!(
          trackHubModalConfig?.isEnabled ??
          activeAddOn?.enabled ??
          activeAddOn?.status === "Active"
        )
      : activeAddOn?.status === "Active";

  const trackHubLinkedAt =
    activeAddOn?.addon_type === "TRACK_HUB"
      ? trackHubModalConfig?.integratedAt ||
        trackHubModalConfig?.enabledAt ||
        activeAddOn?.enabledAt ||
        branchStatusData?.enabledAt
      : activeAddOn?.enabledAt;

  const formatModalLinkedAt = (value) => {
    if (!value) return "—";
    const parts = formatLocalizedDateTimeParts(value, resolveDateLocale(i18n.language));
    return parts?.dateTime || new Date(value).toLocaleString();
  };

  const trackHubActivateOptions = React.useMemo(() => {
    const base = (trackHubModalConfig?.activateOptions || []).map((opt) => ({
      ...opt,
      label: getLocalizedPlatformName(opt.appType || opt.label, t),
    }));
    return [
      ...base,
      {
        appId: MANUAL_SHIPMENT_TYPE,
        appType: MANUAL_SHIPMENT_TYPE,
        label: t("trackHubBranchesPage.manualShipments"),
      },
    ];
  }, [trackHubModalConfig, t]);

  const trackHubActivateValue = React.useMemo(() => {
    const sources = [
      ...(trackHubModalConfig?.orderSources ||
        trackHubModalConfig?.activatedAddons ||
        []),
    ];
    if (trackHubModalConfig?.allowManualShipment) {
      sources.push(MANUAL_SHIPMENT_TYPE);
    }
    return sources;
  }, [trackHubModalConfig]);

  const trackHubShipmentCarriers = React.useMemo(() => {
    if (trackHubAccountCarriers.length) return trackHubAccountCarriers;
    return (trackHubModalConfig?.carriers || []).map((c) => ({
      carrierId: c.carrierId || c.id,
      name: c.name || c.carrierId || c.id,
      isActive: true,
    }));
  }, [trackHubAccountCarriers, trackHubModalConfig]);

  const handleTrackHubActivateChange = async (nextTypes) => {
    if (!canManageAddonSettings) {
      toast.error(noPermissionTip);
      return;
    }

    setSavingTrackHubModal(true);
    try {
      const allowManualShipment = nextTypes.includes(MANUAL_SHIPMENT_TYPE);
      const orderSources = nextTypes.filter((type) => type !== MANUAL_SHIPMENT_TYPE);
      if (!orderSources.length && !allowManualShipment) {
        await patchTrackHubBranch(branchId, { enabled: false });
        toast.success(t("trackHubBranchesPage.removed"));
      } else {
        await patchTrackHubBranch(branchId, {
          orderSources,
          allowManualShipment,
          enabled: true,
        });
        toast.success(t("trackHubBranchesPage.updated"));
      }

      const thResult = await fetchTrackHubBranches({ branchId, limit: 1 });
      const trackHubRow = thResult.data?.[0] || null;
      setTrackHubModalConfig(trackHubRow);
      if (trackHubRow && activeAddOn?.addOnAppId) {
        const isEnabled =
          (trackHubRow.orderSources || []).length > 0 ||
          trackHubRow.allowManualShipment === true;
        setAddOnStatuses((prev) => ({
          ...prev,
          [activeAddOn.addOnAppId]: {
            status: isEnabled ? "Active" : "Inactive",
            isConnected: isEnabled,
          },
        }));
        setActiveAddOn((prev) =>
          prev
            ? {
                ...prev,
                enabled: isEnabled,
                status: isEnabled ? "Active" : "Inactive",
                enabledAt: trackHubRow.integratedAt || prev.enabledAt || null,
              }
            : prev
        );
        fetchBranchStatus(activeAddOn.addOnAppId);
      }
      fetchBranchDetails();
    } catch (error) {
      console.error("Failed to update TrackHub activate options", error);
      toast.error(t("trackHubBranchesPage.updateError"));
    } finally {
      setSavingTrackHubModal(false);
    }
  };

  const handleTrackHubShipmentChange = async (nextValue) => {
    if (!canManageAddonSettings) {
      toast.error(noPermissionTip);
      return;
    }
    if (!trackHubModalConfig?.isEnabled && !activeAddOn?.enabled) return;

    setSavingTrackHubModal(true);
    try {
      const isAuto = !nextValue || nextValue === "auto";
      await patchTrackHubBranch(branchId, {
        orderSources:
          trackHubModalConfig?.orderSources ||
          trackHubModalConfig?.activatedAddons ||
          [],
        allowManualShipment: trackHubModalConfig?.allowManualShipment === true,
        shipmentAssignment: isAuto ? "auto" : "manual",
        assignedCarrierId: isAuto ? null : nextValue,
      });
      const thResult = await fetchTrackHubBranches({ branchId, limit: 1 });
      const trackHubRow = thResult.data?.[0] || null;
      setTrackHubModalConfig(trackHubRow);
      if (trackHubRow?.integratedAt) {
        setActiveAddOn((prev) =>
          prev ? { ...prev, enabledAt: trackHubRow.integratedAt } : prev
        );
      }
      toast.success(t("trackHubBranchesPage.updated"));
    } catch (error) {
      console.error("Failed to update TrackHub shipment assignment", error);
      toast.error(t("trackHubBranchesPage.updateError"));
    } finally {
      setSavingTrackHubModal(false);
    }
  };

  const fetchBranchStatus = async (addOnAppId) => {
    setLoadingBranchStatus(true);
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/add-ons/branches/status", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          addOnAppId,
          limit: "1000",
          ...(storeId ? { storeId } : {}),
        },
      });
      
      // Filter for the current branch
      const currentBranchData = response.data?.data?.find(
        (b) => b.branchId === branchId
      );
      
      console.log("✅ Branch Status Data:", response.data);
      console.log("✅ Current Branch Data:", currentBranchData);
      
      if (currentBranchData) {
        setBranchStatusData(currentBranchData);
        // Set slug value if available
        if (currentBranchData.slug) {
          setSlugValue(currentBranchData.slug);
        }
        // Update activeAddOn with the integrated catalog ID, auto_accept, enabled status, isConnected, and enabledAt
        // Preserve integrationId and other fields from prev
        setActiveAddOn(prev => {
          if (!prev) return prev;
          
          const updated = {
            ...prev,
            catalog_id: currentBranchData.integratedCatalogId || null,
            auto_accept: currentBranchData.autoOrderAccept !== undefined 
              ? currentBranchData.autoOrderAccept 
              : prev.auto_accept,
            enabled: currentBranchData.isConnected !== undefined 
              ? currentBranchData.isConnected 
              : prev.enabled,
            isConnected: currentBranchData.isConnected !== undefined 
              ? currentBranchData.isConnected 
              : prev.isConnected,
            enabledAt: currentBranchData.isConnected ? currentBranchData.enabledAt : null,
            status: currentBranchData.isConnected ? "Active" : "Inactive"
          };
          
          console.log("🔄 Updated activeAddOn after fetchBranchStatus:", updated);
          console.log("🔑 integrationId preserved:", updated.integrationId);
          console.log("🔑 enabled status:", updated.enabled);
          console.log("🔑 isConnected:", updated.isConnected);
          console.log("🔑 enabledAt:", updated.enabledAt);
          
          return updated;
        });
      }
    } catch (error) {
      console.error("❌ Error fetching branch status:", error);
      toast.error(
        error.response?.data?.message ||
          t("Failed to fetch branch status. Please try again.")
      );
    } finally {
      setLoadingBranchStatus(false);
    }
  };

  const handleAppStatusChange = async (app, latestSync, newStatus) => {
    if (!canUpdateBranchAppStatus) {
      toast.error(noPermissionTip);
      return;
    }
    const appId = app._id;
    setTogglingSync((prev) => ({ ...prev, [appId]: true }));

    try {
      const token = localStorage.getItem("token");
      const payload = {
        appName: app.application,
        externalId: latestSync.external_id,
        appId: latestSync.integrationId,
        status: newStatus,
      };

      console.log("🔄 Updating App Status:", payload);

      const response = await api.post(`/branch/${branchId}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status >= 200 && response.status < 300) {
        console.log("✅ Status updated successfully:", response.data);
        toast.success(`${t(response.data.message)}`);
        await fetchBranchDetails();
      } else {
        throw new Error(`Server responded with status ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error updating status:", error);
      toast.error(
        `Failed to update status for ${app.application}. ${
          error.response?.data?.message || "Please try again later."
        }`
      );
    } finally {
      setTogglingSync((prev) => ({ ...prev, [appId]: false }));
    }
  };

  const renderEditableField = (value, onChange, placeholder = "") => (
    <NativeInput
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="form-control-sm"
    />
  );

  const handleSlotFieldChange = (field, index, subField, value) => {
    setTempBranch((prev) => {
      const updated = { ...prev };
      updated[field][index][subField] = value;
      return updated;
    });
    // Validate slot
    validateSlotField(field, index, subField, value);
  };

  const validateSlotField = (field, index, subField, value) => {
    setSlotErrors((prev) => {
      const key = `${field}_${index}`;
      const slot = tempBranch[field][index];
      let errors = prev[key] || { day: "", start: "", end: "" };
      if (subField === "day") {
        errors.day = !value ? `${t("Day is required")}` : "";
      }
      if (subField === "start" || subField === "end") {
        const start = subField === "start" ? value : slot.start;
        const end = subField === "end" ? value : slot.end;
        if (!isValidTime(start)) {
          errors.start = `${t("Start time must be in HH:MM format")}`;
        } else {
          errors.start = "";
        }
        if (!isValidTime(end)) {
          errors.end = `${t("End time must be in HH:MM format")}`;
        } else {
          errors.end = "";
        }
        if (isValidTime(start) && isValidTime(end) && start >= end) {
          errors.start = `${t("Start time must be before end time")}`;
          errors.end = `${t("End time must be after start time")}`;
        }
      }
      return { ...prev, [key]: errors };
    });
  };

  const getLatestSyncForApp = (appName) => {
    if (!branch?.syncedApps) return null;
    // Filter for this app
    const syncs = branch.syncedApps.filter(
      (s) => s.app && s.app.toLowerCase() === appName.toLowerCase()
    );
    if (!syncs.length) return null;
    // Return the latest by synced_date
    return syncs.reduce((latest, curr) => {
      if (!latest) return curr;
      return new Date(curr.synced_date) > new Date(latest.synced_date)
        ? curr
        : latest;
    }, null);
  };

  const renderTimeSlotTables = () => {
    const fields = [
      // { name: "breakHours", title: "Break Hours" },
      // { name: "holidayHours", title: "Holiday Hours" },
    ];

    return (
      <div className="row p-0 g-4">
        {fields.map((field) => (
          <div className="col-12 col-md-6" key={field.name}>
            <MDBCard className="h-100">
              <MDBCardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <MDBCardTitle>{field.title}</MDBCardTitle>
                  {editMode && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleAddSlot(field.name)}
                      disabled={addingSlot === field.name}
                    >
                      <MDBIcon icon="plus" className="me-1" />
                      Add
                    </Button>
                  )}
                </div>

                <MDBTable responsive>
                  <MDBTableHead>
                    <tr>
                      <th>{t("Day")}</th>
                      <th>{t("Start Time")}</th>
                      <th>{t("End Time")}</th>
                      {editMode && <th>{t("actions")}</th>}
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {tempBranch[field.name]?.map((slot, index) => {
                      const slotKey = `${field.name}_${index}`;
                      const errors = slotErrors[slotKey] || {};
                      return (
                        <tr key={slot._id || index}>
                          <td>
                            {editMode ? (
                              <>
                                <select
                                  className="form-select form-select-sm"
                                  value={slot.day || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "day",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Day</option>
                                  {DAYS_OF_WEEK.map((day) => (
                                    <option key={day} value={day}>
                                      {day.charAt(0).toUpperCase() +
                                        day.slice(1)}
                                    </option>
                                  ))}
                                </select>
                                {errors.day && (
                                  <div className="text-danger small mt-1">
                                    {errors.day}
                                  </div>
                                )}
                              </>
                            ) : (
                              slot.day || "N/A"
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={slot.start || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "start",
                                      e.target.value
                                    )
                                  }
                                />
                                {errors.start && (
                                  <div className="text-danger small mt-1">
                                    {errors.start}
                                  </div>
                                )}
                              </>
                            ) : (
                              formatTime(slot.start)
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={slot.end || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "end",
                                      e.target.value
                                    )
                                  }
                                />
                                {errors.end && (
                                  <div className="text-danger small mt-1">
                                    {errors.end}
                                  </div>
                                )}
                              </>
                            ) : (
                              formatTime(slot.end)
                            )}
                          </td>
                          {editMode && (
                            <td>
                              {addingSlot === field.name && index === 0 ? (
                                <>
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="me-2"
                                    onClick={() =>
                                      handleSaveNewSlot(field.name)
                                    }
                                  >
                                    <MDBIcon icon="check" />
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() =>
                                      handleCancelAddSlot(field.name)
                                    }
                                  >
                                    <MDBIcon icon="times" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteSlot(field.name, index)
                                  }
                                  disabled={addingSlot === field.name}
                                >
                                  <MDBIcon icon="trash" />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </MDBTableBody>
                </MDBTable>
              </MDBCardBody>
            </MDBCard>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !editMode) {
    return (
      <div className="view-branch-page-root oh-page">
        <MDBContainer fluid className="content-area flex-grow-1">
          <h1 className="page-title-header">{t("branchdetails")}</h1>
          <div className="text-center my-5">
            <MDBSpinner role="status">
              <span className="visually-hidden">{t("loading")}...</span>
            </MDBSpinner>
            <p>{t("Loading branch details")}...</p>
          </div>
        </MDBContainer>
      </div>
    );
  }

  if (
    error &&
    (!branch ||
      (!error.includes("not found") &&
        !error.includes("Missing store or branch ID")))
  ) {
    return (
      <div className="view-branch-page-root oh-page">
        <MDBContainer fluid className="content-area flex-grow-1">
          <h1 className="page-title-header">{t("branchdetails")}</h1>
          <div className="text-center my-5">
            <MDBIcon
              icon="exclamation-triangle"
              size="2x"
              className="text-danger"
            />
            <p className="text-danger">{error}</p>
          </div>
        </MDBContainer>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="view-branch-page-root oh-page">
        <MDBContainer fluid className="content-area flex-grow-1">
          <h1 className="page-title-header">{t("branchdetails")}</h1>
          <div className="text-center my-5">
            <MDBIcon icon="question-circle" size="2x" />
            <p>{t("Branch not found")}</p>
          </div>
        </MDBContainer>
      </div>
    );
  }

  const branchDisplayName = getBranchDisplayName(branch, isArabic);

  return (
    <div className="view-branch-page-root oh-page">
    <MDBContainer fluid className="content-area flex-grow-1">
      <h1 className="page-title-header">
        {branchDisplayName || t("branchdetails")}
      </h1>
      {storeId ? (
        <p className="oh-page-subtitle">
          {t("Store ID")}: {storeId}
        </p>
      ) : null}

      <div className="oh-page-section">
      <Actionbar
        isViewBranch={true}
        title={t("quickAction")}
        subtitle={t("manageYouWorkSpace")}
        firstBtnText={editMode ? `${t("cancel")}` : `${t("back")}`}
        firstBtnOnClick={
          editMode
            ? () => handleEditToggle() // Cancel editing
            : () => navigate(-1) // Go back
        }
        firstBtnClass={editMode ? "reject-orders" : "secondary"}
        firstBtnIcon={editMode ? "cross" : "back"}
        secondBtnText={editMode ? `${t("savechanges")}` : `${t("editbranch")}`}
        secondBtnOnClick={
          editMode
            ? () => {
                handleSaveChanges(); // Save changes
              }
            : () => {
                if (!canManageBranchMappings) {
                  toast.error(noPermissionTip);
                  return;
                }
                setEditMode(true);
              }
        }
        secondBtnClass={editMode ? "accept-orders" : "ocean"}
        secondBtnIcon={editMode ? "save" : "pen"}
        disabledBtn2={
          editMode
            ? Object.values(fieldErrors).some((error) => error !== "")
            : !canManageBranchMappings
        }
        secondBtnTitle={
          !editMode && !canManageBranchMappings ? noPermissionTip : undefined
        }
      />
      {isArabic && (
        <style>{`
          .actionbar-buttons .actionbar-btn {
            direction: ltr;
          }
        `}</style>
      )}
      </div>

      <style>{`
        @media (max-width: 1130px) {
          .branch-row > div {
            width: 100% !important;
            flex: 0 0 100% !important;
            max-width: 100% !important;
            margin-bottom: 24px;
          }
          .branch-row > div:last-child {
            margin-bottom: 0;
          }
        }
        @media (min-width: 1271px) {
          .integration-card-col {
            flex: 0 0 25%;
            width: 25%;
          }
        }
      `}</style>

      <div className="oh-page-section">
      <MDBRow
        className="g-3 branch-row"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <MDBCol lg="5" md="6" sm="12" style={{ marginTop: "0px" }}>
          <MDBCard className="branch-info-card shadow-sm border-0 h-100">
            <MDBCardBody>
              {editMode ? (
                <div className="branch-info-edit">
                  <div className="branch-info-edit__section">
                    <div>
                      <input
                        type="text"
                        className="branch-info-edit__name"
                        value={
                          typeof tempBranch?.name === "object"
                            ? tempBranch?.name?.en || ""
                            : tempBranch?.name || ""
                        }
                        onChange={(e) =>
                          handleFieldChangeValidated("name", e.target.value)
                        }
                        aria-label={t("branchnameenglish")}
                      />
                      {fieldErrors.name && (
                        <div className="branch-info-edit__error mt-1">
                          {fieldErrors.name}
                        </div>
                      )}
                    </div>

                    <div className="branch-info-edit__rows">
                      {[
                        {
                          label: t("email"),
                          field: "email",
                          value: tempBranch?.email || "",
                        },
                        {
                          label: t("phone"),
                          field: "phone",
                          value: tempBranch?.phone || "",
                        },
                        {
                          label: t("branchnamearabic"),
                          field: "name_ar",
                          value:
                            tempBranch?.name_ar ||
                            tempBranch?.name?.ar ||
                            "",
                        },
                      ].map((item) => (
                        <div className="branch-info-edit__row" key={item.field}>
                          <span className="branch-info-edit__label">
                            {item.label}
                          </span>
                          <div className="branch-info-edit__field">
                            <input
                              type="text"
                              className="branch-info-edit__control"
                              value={item.value}
                              onChange={(e) =>
                                handleFieldChangeValidated(
                                  item.field,
                                  e.target.value
                                )
                              }
                            />
                            {fieldErrors[item.field] && (
                              <div className="branch-info-edit__error">
                                {fieldErrors[item.field]}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr className="branch-info-edit__divider" />

                  <div className="branch-info-edit__section">
                    <h6 className="branch-info-edit__section-title">
                      {t("Location")}
                    </h6>
                    <div className="branch-info-edit__rows">
                      <div className="branch-info-edit__row">
                        <span className="branch-info-edit__label">
                          {t("city")}
                        </span>
                        <div className="branch-info-edit__field branch-info-edit__field--select">
                          <CustomDropdown
                            options={cities.map((c) => ({
                              _id: c.name,
                              name: c.name,
                              displayName: c.name_localized
                                ? `${c.name} (${c.name_localized})`
                                : c.name,
                            }))}
                            handleSelect={(selected) =>
                              handleFieldChangeValidated(
                                "city",
                                selected?._id || ""
                              )
                            }
                            selectedOption={tempBranch?.city || ""}
                            placeholder={t("Select city")}
                            showLable={false}
                            showClear={true}
                            showIcon={true}
                            disabled={loadingCities}
                            invalid={!!fieldErrors.city}
                            className="branch-info-edit__city-select"
                          />
                          {fieldErrors.city && (
                            <div className="branch-info-edit__error">
                              {fieldErrors.city}
                            </div>
                          )}
                        </div>
                      </div>
                      {[
                        {
                          label: t("address"),
                          field: "address",
                        },
                        {
                          label: t("latitude"),
                          field: "latitude",
                        },
                        {
                          label: t("longitude"),
                          field: "longitude",
                        },
                      ].map((item) => (
                        <div className="branch-info-edit__row" key={item.field}>
                          <span className="branch-info-edit__label">
                            {item.label}
                          </span>
                          <div className="branch-info-edit__field">
                            <input
                              type="text"
                              className="branch-info-edit__control"
                              value={tempBranch?.[item.field] || ""}
                              onChange={(e) =>
                                handleFieldChangeValidated(
                                  item.field,
                                  e.target.value
                                )
                              }
                            />
                            {fieldErrors[item.field] && (
                              <div className="branch-info-edit__error">
                                {fieldErrors[item.field]}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr className="branch-info-edit__divider" />
                </div>
              ) : (
                <div className="branch-fields">
                  {[
                    {
                      label: t("branchnameenglish"),
                      field: "name",
                      value: branch.name?.en || branch.name,
                    },
                    {
                      label: t("branchnamearabic"),
                      field: "name_ar",
                      value: branch.name?.ar || branch.name_ar,
                    },
                    { label: t("email"), field: "email", value: branch.email },
                    { label: t("phone"), field: "phone", value: branch.phone },
                  ].map((item) => (
                    <div
                      className="d-flex align-items-start mb-3"
                      key={item.field}
                    >
                      <div
                        className="text-dark fw-bold"
                        style={{ minWidth: "200px" }}
                      >
                        {item.label}
                      </div>
                      <div className="flex-fill">
                        <div style={{ fontSize: "14px" }}>
                          {item.field === "name_ar"
                            ? String(item.value || "").trim() || "NA"
                            : item.value || ""}
                        </div>
                      </div>
                    </div>
                  ))}

                  <hr className="my-3 mx-3 text-muted opacity-25" />

                  <h6 className="oh-form-section__title mt-2">
                    {t("Location")}
                  </h6>

                  {[
                    {
                      label: t("city"),
                      field: "city",
                      value: branch.city,
                    },
                    {
                      label: t("address"),
                      field: "address",
                      value: branch.address,
                    },
                    {
                      label: t("latitude"),
                      field: "latitude",
                      value: branch.latitude,
                    },
                    {
                      label: t("longitude"),
                      field: "longitude",
                      value: branch.longitude,
                    },
                  ].map((item) => (
                    <div className="d-flex mb-2" key={item.field}>
                      <div
                        className="text-dark fw-bold"
                        style={{ minWidth: "200px" }}
                      >
                        {item.label}
                      </div>
                      <div className="flex-fill">
                        <div style={{ fontSize: "14px" }}>
                          {item.value || "—"}
                        </div>
                      </div>
                    </div>
                  ))}
                  <hr className="my-3 mx-3 text-muted opacity-25" />
                </div>
              )}

              <div className="branch-info-card__integration-id">
                <span className="branch-info-card__integration-id-text">
                  {t("integrationid")}: {branch._id}
                </span>
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted"
                  style={{ lineHeight: 1 }}
                  onClick={() => {
                    navigator.clipboard.writeText(branch._id);
                    setCopiedId(branch._id);
                    setTimeout(() => setCopiedId(null), 2000);
                  }}
                  aria-label={t("copy")}
                >
                  {copiedId === branch._id ? (
                    <Check size={16} className="text-success" />
                  ) : (
                    <Files size={16} color="#757575" />
                  )}
                </button>
              </div>
            </MDBCardBody>
          </MDBCard>
          <IntegrationMoreModal
            show={showMoreModal}
            onHide={() => setShowMoreModal(false)}
            app={activeApp}
            latestSync={
              activeApp ? getLatestSyncForApp(activeApp.application) : null
            }
            t={t}
            branchId={branchId}
            storeId={storeId}
            onSyncChange={fetchBranchDetails}
            integrationMutationsDisabled={!canUpdateBranchAppStatus}
            branchMappingMutationsDisabled={!canManageBranchMappings}
            autoOrderMutationsDisabled={!canManageAutoAccessOrders}
            isFoodicsIntegrated={isBranchFoodicsIntegrated(branch)}
            isTabSenseIntegrated={isBranchTabSenseIntegrated(branch)}
            branch={branch}
            isPending={
              activeApp && getLatestSyncForApp(activeApp.application)
                ? getLatestSyncForApp(activeApp.application)?.synced_status &&
                  getLatestSyncForApp(activeApp.application)?.external_id ===
                    null
                : false
            }
          />
        </MDBCol>
        <MDBCol lg="7" md="6" sm="12" style={{ marginTop: "0px" }}>
          <AvailabilityScheduleCard
            availableHours={tempBranch.availableHours}
            onEditClick={handleOpenEditAvailability}
            isEditDisabled={branch?.syncedApps?.some(
              (sync) => sync.app?.toUpperCase() === "FOODICS"
            )}
            showEditButton={editMode && canManageBranchMappings}
          />
        </MDBCol>
      </MDBRow>
      </div>

      <div className="oh-page-section">
        <MDBRow>
          <MDBCol>
            <MDBCard
              className="border-0 shadow-sm rounded-4"
              style={{ backgroundColor: "#fff", marginBottom: "0px" }}
            >
              <MDBCardBody style={{ padding: "24px" }}>
                <h2 className="oh-form-section__title">
                  {t("Add-Ons")}
                </h2>

                <div className="row g-4">
                  {addOnApps.map((app) => (
                    <div key={app._id} className="col-12 col-md-6 col-lg-4 integration-card-col">
                      <div 
                        className="p-3 border rounded-3 h-100 shadow-sm bg-white border-light"
                      >
                        {/* Header: Logo + Title + Status */}
                        <div
                          className="d-flex align-items-start gap-3"
                          style={{ marginBottom: "28px" }}
                        >
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center overflow-hidden"
                            style={{
                              width: usesWideIntegrationLogo(app) ? "72px" : "48px",
                              height: "48px",
                            }}
                          >
                            <img
                              src={resolveIntegrationLogo(app) || app.logo || LOOPS_LOGO_SRC}
                              alt={app.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                 objectFit: "contain"
                              }}
                            />
                          </div>
                          <div>
                            <h6
                              className="mb-0 fw-bold text-dark"
                              style={{ fontSize: "12px" }}
                            >
                              {isArabic
                                ? String(app.name_ar || "").trim() || "NA"
                                : app.name}
                            </h6>
                            {(() => {
                              const addOnStatus = addOnStatuses[app._id];
                              const isConnected = addOnStatus?.isConnected || false;
                              const status = addOnStatus?.status || "Inactive";
                              
                              if (app.isConnected && status === "Active") {
                                return (
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <MDBIcon
                                      icon="check-circle"
                                      style={{
                                        fontSize: "10px",
                                        color: "#00D69F",
                                      }}
                                    />
                                    <small
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#00D69F",
                                      }}
                                    >
                                      {t("integrated")}
                                    </small>
                                  </div>
                                );
                              } else if (app.isConnected && status === "Inactive") {
                                return (
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <MDBIcon
                                      icon="times-circle"
                                      style={{
                                        fontSize: "10px",
                                        color: "#E84A4A",
                                      }}
                                    />
                                    <small
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#E84A4A",
                                      }}
                                    >
                                      {t("Not Integrated")}
                                    </small>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <MDBIcon
                                      icon="times-circle"
                                      style={{
                                        fontSize: "10px",
                                        color: "#8C9196",
                                      }}
                                    />
                                    <small
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#8C9196",
                                      }}
                                    >
                                      {t("Not Available")}
                                    </small>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>

                        {/* Footer Actions: Manage + More */}
                        <div className="d-flex gap-2 pt-2 justify-content-center">
                          {app.isConnected ? (
                            canManageAddonSettings ? (
                              <button
                                type="button"
                                className="flex-grow-1 btn-outline-light text-dark border btn-sm py-2 fw-bold rounded-2"
                                onClick={async () => {
                                console.log("🔹 Opening modal for app:", app);
                                
                                // Get integration data from syncedApps
                                const integrationData = getAddOnIntegrationData(app._id);
                                console.log("🔹 Integration data from syncedApps:", integrationData);
                                
                                setActiveAddOn({
                                  ...app,
                                  addon_type: app.appType,
                                  addOnAppId: app._id, // For branches/status endpoint
                                  integrationId: integrationData?.integrationId, // For auto-order-status endpoint
                                  external_id: integrationData?.external_id,
                                  auto_accept: integrationData?.auto_order_accept || false
                                });
                                
                                console.log("🔹 Set activeAddOn with integrationId:", integrationData?.integrationId);
                                
                                setSlugValue("");
                                setSlugError("");
                                setSlugValid(false);
                                
                                // Fetch branch status data first using addOnAppId
                                await fetchBranchStatus(app._id);

                                if (app.appType === "TRACK_HUB") {
                                  try {
                                    const thResult = await fetchTrackHubBranches({
                                      branchId,
                                      limit: 1,
                                    });
                                    const trackHubRow = thResult.data?.[0] || null;
                                    setTrackHubModalConfig(trackHubRow);
                                    if (trackHubRow) {
                                      setActiveAddOn((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              enabled: trackHubRow.isEnabled,
                                              status: trackHubRow.isEnabled
                                                ? "Active"
                                                : "Inactive",
                                              enabledAt:
                                                trackHubRow.integratedAt ||
                                                prev.enabledAt ||
                                                null,
                                            }
                                          : prev
                                      );
                                    }
                                  } catch (error) {
                                    console.error("Failed to load TrackHub branch config", error);
                                    setTrackHubModalConfig(null);
                                  }
                                  try {
                                    const carriersResult = await fetchTrackHubCarriers({
                                      refresh: false,
                                    });
                                    setTrackHubAccountCarriers(
                                      (carriersResult.data || []).filter(
                                        (c) => c.isActive !== false
                                      )
                                    );
                                  } catch (carriersError) {
                                    console.error(
                                      "Failed to load TrackHub carriers",
                                      carriersError
                                    );
                                    setTrackHubAccountCarriers([]);
                                  }
                                } else {
                                  setTrackHubModalConfig(null);
                                  setTrackHubAccountCarriers([]);
                                }
                                
                                // Then open modal after data is loaded
                                setShowAddOnModal(true);
                              }}
                            >
                              {t("More")}
                              </button>
                            ) : (
                              <MDBTooltip tag="span" title={noPermissionTip} wrapperProps={{ className: "flex-grow-1 d-inline-block" }}>
                                <span className="d-flex w-100">
                                  <button
                                    type="button"
                                    className="flex-grow-1 btn-outline-light text-dark border btn-sm py-2 fw-bold rounded-2"
                                    disabled
                                    style={{
                                      cursor: "not-allowed",
                                      opacity: 0.65,
                                      pointerEvents: "none",
                                    }}
                                  >
                                    {t("More")}
                                  </button>
                                </span>
                              </MDBTooltip>
                            )
                          ) : (
                            <MDBTooltip
                              tag="span"
                              title={addonNotEnabledTip}
                              wrapperProps={{ className: "flex-grow-1 d-inline-block" }}
                            >
                              <span className="d-flex w-100">
                                <button
                                  type="button"
                                  className="flex-grow-1 btn-outline-light text-dark border btn-sm py-2 fw-bold rounded-2"
                                  disabled
                                  style={{
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    pointerEvents: "none",
                                  }}
                                >
                                  {t("Contact Admin")}
                                </button>
                              </span>
                            </MDBTooltip>
                          )}
                        
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <style>{`
                  .addon-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
                  }
                `}</style>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </div>

      <div className="oh-page-section">
      <MDBRow>
        <MDBCol>
          <MDBCard
            className="border-0 shadow-sm rounded-4"
            style={{ backgroundColor: "#fff" }}
          >
            <MDBCardBody style={{ padding: "24px" }}>
              <h2 className="oh-form-section__title">
                {t("integration")}
              </h2>

              <div className="row g-4">
                {apps
                  .filter((app) => app.store?._id === storeId)
                  .map((app) => {
                    const latestSync = getLatestSuccessfulSync(branch).find(
                      (sync) => sync.app === app.application
                    );
                
                    const isIntegrated = !!latestSync;
                    const isAvailable =
                      latestSync?.synced_status &&
                      latestSync?.external_id !== null;
                    const isPending =
                      latestSync?.synced_status &&
                      latestSync?.external_id === null;
                    const posConflictKey = branchPosConflictTooltipKey(
                      branch,
                      app.application
                    );
                    const posIntegrateBlocked =
                      !isIntegrated && !!posConflictKey;

                    return (
                      <div
                        key={app._id}
                        className="col-12 col-md-6 col-lg-4 integration-card-col"
                      >
                        <div className="p-3 border rounded-3 h-100 shadow-sm bg-white border-light">
                          {/* Header: Logo + Title + Status */}
                          <div
                            className="d-flex align-items-start gap-3"
                            style={{ marginBottom: "28px" }}
                          >
                            {getAppLogo(app.application) && (
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center overflow-hidden"
                                style={{
                                  width: "48px",
                                  height: "48px",
                                }}
                              >
                                {/* Render App-specific Icon/Image here */}
                                <img
                                  src={getAppLogo(app.application)}
                                  alt={app.application}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                  }}
                                />
                              </div>
                            )}
                            <div>
                              <h6
                                className="mb-0 fw-bold text-dark"
                                style={{ fontSize: "12px" }}
                              >
                                {app.application}
                              </h6>
                              <div className="d-flex align-items-center gap-1 mt-2">
                                <MDBIcon
                                  icon={
                                    isIntegrated && !isPending
                                      ? "check-circle"
                                      : "exclamation-circle"
                                  }
                                  style={{
                                    fontSize: "10px",
                                    color:
                                      isIntegrated && !isPending
                                        ? "#00D69F"
                                        : "#dc3545",
                                  }}
                                />
                                <small
                                  className=""
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color:
                                      isIntegrated && !isPending
                                        ? "#00D69F"
                                        : "#dc3545",
                                  }}
                                >
                                  {isIntegrated && !isPending
                                    ? t("integrated")
                                    : t("Not Integrated")}
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Footer Actions: Toggle + More */}
                          <div className="d-flex gap-2 pt-2">
                            {!app.application?.toLowerCase().includes("foodics") &&
                              !app.application?.toLowerCase().includes("tabsense") && (
                              <>
                                {isIntegrated && !isPending && (
                                  <button
                                    className={`flex-grow-1 btn-sm border-0 py-2 fw-bold rounded-2 ${
                                      isAvailable
                                        ? "accept-orders-btn"
                                        : "reject-orders-btn"
                                    }`}
                                    disabled={
                                      togglingSync[app._id] || !canUpdateBranchAppStatus
                                    }
                                    title={
                                      !canUpdateBranchAppStatus ? noPermissionTip : undefined
                                    }
                                    onClick={() =>
                                      handleAppStatusChange(
                                        app,
                                        latestSync,
                                        !isAvailable
                                      )
                                    }
                                  >
                                    {togglingSync[app._id] ? (
                                      <MDBSpinner size="sm" />
                                    ) : isAvailable ? (
                                      t("Available")
                                    ) : (
                                      t("Unavailable")
                                    )}
                                  </button>
                                )}

                                {isPending && (
                                  <button
                                    className="flex-grow-1 btn-sm border-0 py-2 fw-bold rounded-2 reject-orders-btn"
                                    disabled
                                    title={t(
                                      "Branch is pending approval from the platform"
                                    )}
                                  >
                                    {t("Unavailable")}
                                  </button>
                                )}

                                {!isIntegrated && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id={`tooltip-${app._id}`}>
                                        {t("appNotIntegratedTooltip")}
                                      </Tooltip>
                                    }
                                  >
                                    <button
                                      className="flex-grow-1 btn-sm border-0 py-2 fw-bold rounded-2 reject-orders-btn"
                                      disabled={true}
                                    >
                                      {t("Unavailable")}
                                    </button>
                                  </OverlayTrigger>
                                )}
                              </>
                            )}

                            {posIntegrateBlocked ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>{t(posConflictKey)}</Tooltip>
                                }
                              >
                                <span className="flex-grow-1 d-inline-block">
                                  <button
                                    type="button"
                                    className="btn-outline-light text-dark border btn-sm py-2 fw-bold rounded-2 w-100"
                                    disabled
                                    style={{
                                      cursor: "not-allowed",
                                      opacity: 0.65,
                                    }}
                                  >
                                    {t("More")}
                                  </button>
                                </span>
                              </OverlayTrigger>
                            ) : (
                            <button
                              className={`btn-outline-light text-dark border btn-sm py-2 fw-bold rounded-2 flex-grow-1`}
                              onClick={() => handleOpenMore(app, latestSync)}
                            >
                              {t("More")}
                            </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      </div>

      <EditAvailabilityDrawer
        show={showEditAvailabilityModal}
        onHide={handleCloseAvailabilityModal}
        availableHours={tempBranch.availableHours}
        onSave={handleSaveAvailability}
        branchId={branchId}
        hasKeetaSync={branch?.syncedApps?.some((a) => a.app === "Keeta")}
        // hasFoodicsSync={branch?.syncedApps?.some((a) => a.app === "Foodics")}
      />

      {/* Add-On Modal */}
      <Modal
        show={showAddOnModal}
        onHide={() => {
          setShowAddOnModal(false);
          setActiveAddOn(null);
          setTrackHubModalConfig(null);
          setTrackHubAccountCarriers([]);
          setSavingTrackHubModal(false);
          setSlugError("");
          setSlugValid(false);
        }}
        centered
        className={`addon-modal integration-modal ${
          activeAddOn?.addon_type === "TRACK_HUB" ? "trackhub-branch-modal" : ""
        }`}
      >
        {activeAddOn?.addon_type === "TRACK_HUB" ? (
          <>
            <Modal.Header closeButton className="trackhub-branch-modal__header">
              <Modal.Title className="trackhub-branch-modal__title">
                {t("trackHubBranchModal.title")}
              </Modal.Title>
            </Modal.Header>

            <hr className="trackhub-branch-modal__divider" />

            <Modal.Body className="trackhub-branch-modal__body">
              <div className="trackhub-branch-modal__fields">
                <div className="trackhub-branch-modal__row trackhub-branch-modal__row--status">
                  <span className="trackhub-branch-modal__label">
                    {t("trackHubBranchModal.status")}
                  </span>
                  <span
                    className={`trackhub-branch-modal__status-badge ${
                      trackHubModalIsActive
                        ? "trackhub-branch-modal__status-badge--active"
                        : "trackhub-branch-modal__status-badge--inactive"
                    }`}
                  >
                    {trackHubModalIsActive ? t("Active") : t("Inactive")}
                  </span>
                </div>

                <div className="trackhub-branch-modal__row">
                  <span className="trackhub-branch-modal__label">
                    {t("trackHubBranchModal.linkedAt")}
                  </span>
                  <span className="trackhub-branch-modal__value">
                    {formatModalLinkedAt(trackHubLinkedAt)}
                  </span>
                </div>

                <div className="trackhub-branch-modal__row trackhub-branch-modal__row--field">
                  <span className="trackhub-branch-modal__label trackhub-branch-modal__label--field">
                    {t("trackHubBranchModal.activate")}
                  </span>
                  <div className="trackhub-branch-modal__control">
                    <TrackHubActivateSelect
                      options={trackHubActivateOptions}
                      value={trackHubActivateValue}
                      inactive={
                        !trackHubModalConfig?.isEnabled &&
                        !trackHubModalConfig?.allowManualShipment &&
                        !activeAddOn.enabled
                      }
                      disabled={savingTrackHubModal || !canManageAddonSettings}
                      emptyLabel={t("trackHubBranchesPage.notIntegrated")}
                      allLabel={t("all")}
                      menuZIndex={1060}
                      onChange={handleTrackHubActivateChange}
                    />
                  </div>
                </div>

                <div className="trackhub-branch-modal__row trackhub-branch-modal__row--field trackhub-branch-modal__row--shipment">
                  <span className="trackhub-branch-modal__label trackhub-branch-modal__label--field trackhub-branch-modal__label--shipment">
                    {t("trackHubBranchModal.assignShipment")}
                  </span>
                  <div className="trackhub-branch-modal__control">
                    <TrackHubShipmentSelect
                      value={
                        trackHubModalConfig?.assignedCarrierId ||
                        (trackHubModalConfig?.shipmentAssignment === "manual"
                          ? trackHubModalConfig?.carriers?.[0]?.carrierId || "manual"
                          : "auto")
                      }
                      carriers={trackHubShipmentCarriers}
                      assignedCarrierName={
                        trackHubModalConfig?.assignedCarrierName ||
                        trackHubModalConfig?.carriers?.[0]?.name
                      }
                      inactive={
                        !trackHubModalConfig?.isEnabled &&
                        !trackHubModalConfig?.allowManualShipment &&
                        !activeAddOn.enabled
                      }
                      disabled={savingTrackHubModal || !canManageAddonSettings}
                      notIntegratedLabel={t("trackHubBranchesPage.notIntegrated")}
                      autoLabel={t("trackHubBranchesPage.autoAssign")}
                      manualLabel={t("trackHubBranchesPage.manualAssign")}
                      autoHelp={t("trackHubBranchModal.autoAssignHelp")}
                      menuZIndex={1060}
                      onChange={handleTrackHubShipmentChange}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
          </>
        ) : (
          <>
        <Modal.Header
          closeButton
          className="border-0"
          style={{ padding: "30px" }}
        >
          <Modal.Title
            className="fw-bold"
            style={{ color: "#030F1B", fontSize: "20px" }}
          >
            {activeAddOn?.addon_type === "CHAT_AGENT"
              ? t("Order Agent")
              : t("Digital Catalog")}
          </Modal.Title>
        </Modal.Header>

        <hr className="m-0 opacity-25" />

        <Modal.Body className="p-0">
          <div className="px-5 py-2">
            <div className="mb-3 mt-3">
              {activeAddOn && (
                <>
                  {/* Status */}
                  <div className="d-flex align-items-center mb-3 addon-modal__row">
                    <span className="fw-bold small addon-label" style={{ color: "#030F1B" }}>
                      {t("Status")}
                    </span>
                    <span
                      className="badge rounded px-3 py-2"
                      style={{
                        backgroundColor:
                          activeAddOn.status === "Active" ? "#e8f7f0" : "#fceaea",
                        color:
                          activeAddOn.status === "Active" ? "#00D69F" : "#E84A4A",
                        border: `1px solid ${
                          activeAddOn.status === "Active" ? "#00D69F" : "#E84A4A"
                        }`,
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {activeAddOn.status === "Active" ? t("Active") : t("Inactive")}
                    </span>
                  </div>

                  {/* Linked At */}
                  {activeAddOn.status === "Active" && activeAddOn.enabledAt && (
                    <div className="d-flex align-items-center mb-3 addon-modal__row">
                      <span className="fw-bold small addon-label" style={{ color: "#030F1B" }}>
                        {t("Linked At")}
                      </span>
                      <span className="fw-medium small" style={{ color: "#030F1B" }}>
                        {formatModalLinkedAt(activeAddOn.enabledAt)}
                      </span>
                    </div>
                  )}

                  {/* Enable Integration */}
                  <div className={` ${
                    activeAddOn.enabled ? "is-active" : ""
                  }`}>
                    <div
                      className="d-flex align-items-center justify-content-between mb-3"
                    >
                      <span
                        className="fw-bold small text-dark"
                       
                      >
                        {t("Enable Integration")}
                      </span>
                      <div
                        className={`custom-switch-root ${
                          activeAddOn.enabled ? "is-checked" : ""
                        } ${
                          updatingEnableIntegration ||
                          !canManageAddonSettings ||
                          (!activeAddOn.enabled &&
                            !activeAddOn.catalog_id)
                            ? "is-disabled"
                            : ""
                        }`}
                        style={{ "--active-bg": "#00d69f" }}
                        onClick={() =>
                          !updatingEnableIntegration &&
                          canManageAddonSettings &&
                          (activeAddOn.enabled ||
                            activeAddOn.catalog_id) &&
                          handleEnableIntegrationToggle()
                        }
                      >
                        <div className="custom-switch-thumb" />
                      </div>
                    </div>
                  </div>

                  {/* Catalog */}
                  <div className="d-flex align-items-center mb-3">
                    <span className="fw-bold small addon-label" style={{ color: "#030F1B" }}>
                      {t("AddOnIntegration.catalogs")}
                    </span>
                    <div style={{ flex: 1 }}>
                      <CustomDropdown
                        options={
                          branchStatusData?.catalogs?.map((catalog) => ({
                            _id: catalog.id || catalog._id,
                            name: catalog.name || catalog.name_en || "",
                            name_en: catalog.name_en || catalog.name || "",
                            name_ar: catalog.name_ar || catalog.name || "",
                          })) || []
                        }
                        handleSelect={(selected) =>
                          handleAddOnCatalogUpdate(activeAddOn.addon_type, selected)
                        }
                        selectedOption={activeAddOn.catalog_id || ""}
                        placeholder={
                          branchStatusData?.catalogs?.length
                            ? t("AddOnIntegration.selectCatalog")
                            : t("AddOnIntegration.noCatalogs")
                        }
                        showLable={false}
                        disabled={
                          updatingCatalog[activeAddOn.addon_type] ||
                          !canManageAddonSettings ||
                          !branchStatusData?.catalogs?.length
                        }
                        showClear={true}
                        showIcon={true}
                      />
                    </div>
                  </div>

                  {activeAddOn.addon_type === "DIGITAL_CATALOG" && (
                    <div className="d-flex align-items-start mb-6 pt-4">
                      <span className="fw-bold small addon-label" style={{ color: "#030F1B", paddingTop: "8px" }}>
                        {t("AddOnIntegration.slug")}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="text"
                            className={`form-control ${
                              slugError ? "is-invalid" : slugValid ? "is-valid" : ""
                            }`}
                            value={slugValue}
                            onChange={(e) => {
                              const value = e.target.value.toLowerCase();
                              setSlugValue(value);
                              
                              // Validate slug - prevent empty value
                              if (!value) {
                                setSlugError(t("AddOnIntegration.slugRequired"));
                                setSlugValid(false);
                                // Prevent clearing - restore previous value
                                e.target.value = slugValue || activeAddOn.slug || branchStatusData?.slug || "";
                                return;
                              } else if (!validateSlug(value)) {
                                setSlugError(t("AddOnIntegration.invalidSlugFormat"));
                                setSlugValid(false);
                              } else {
                                setSlugError("");
                                setSlugValid(true);
                              }
                            }}
                            onBlur={() => {
                              if (!slugValue) {
                                setSlugError(t("AddOnIntegration.slugRequired"));
                                setSlugValid(false);
                              } else if (!validateSlug(slugValue)) {
                                setSlugError(t("AddOnIntegration.invalidSlugFormat"));
                                setSlugValid(false);
                              }
                            }}
                            placeholder={t("AddOnIntegration.enterSlug")}
                            style={{ flex: 1 }}
                            disabled={!canManageAddonSettings}
                          />
                          <button
                            className="slug-action-btn slug-save-btn"
                            type="button"
                            onClick={handleSlugUpdate}
                            disabled={updatingSlug || !canManageAddonSettings}
                            title="Save slug"
                          >
                            {updatingSlug ? "..." : "✓"}
                          </button>
                          <button
                            className="slug-action-btn slug-cancel-btn"
                            type="button"
                            onClick={() => {
                              const originalSlug = branchStatusData?.slug || activeAddOn.slug || "";
                              setSlugValue(originalSlug);
                              setSlugError("");
                              setSlugValid(false);
                            }}
                            disabled={updatingSlug || !canManageAddonSettings}
                            title="Cancel editing"
                          >
                            ✕
                          </button>
                        </div>
                        {slugError && (
                          <div className="text-danger small mt-2">
                            {slugError}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Bottom Section - Auto Accept Orders for Chat Agent */}
          {activeAddOn?.addon_type === "CHAT_AGENT" && (
            <>
              <hr className="m-0 opacity-25" />
              <div style={{ padding: "24px 33px 44px 33px" }}>
                <div
                  className={`custom-toggle-container ${
                    activeAddOn.auto_accept ? "is-active" : ""
                  }`}
                >
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-4 mb-0">
                    <span className="label-text">{t("Auto Accept Orders")}</span>
                    {(() => {
                      const posAutoAcceptLocked = isBranchPosAutoAcceptLocked(branch);
                      const partnerManaged = isBranchPartnerManaged(branch);
                      const autoAcceptLocked = !!posAutoAcceptLocked || partnerManaged;
                      const posAutoAcceptTooltip = partnerManaged
                        ? t(branchPartnerAutoAcceptTooltipKey(branch))
                        : t("posAutoAcceptDisabledForMappedBranch");
                      const switchDisabled =
                        updatingAutoAccept ||
                        !canManageAutoAccessOrders ||
                        autoAcceptLocked;
                      const switchEl = (
                        <div
                          className={`custom-switch-root ${
                            activeAddOn.auto_accept ? "is-checked" : ""
                          } ${switchDisabled ? "is-disabled" : ""}`}
                          style={{ "--active-bg": "#00d69f" }}
                          onClick={() =>
                            !switchDisabled && handleAutoAcceptToggle()
                          }
                        >
                          <div className="custom-switch-thumb" />
                        </div>
                      );
                      if (autoAcceptLocked) {
                        return (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip dir={i18n.dir()}>
                                {posAutoAcceptTooltip}
                              </Tooltip>
                            }
                          >
                            <span className="d-inline-block">
                              <span style={{ pointerEvents: "none" }}>
                                {switchEl}
                              </span>
                            </span>
                          </OverlayTrigger>
                        );
                      }
                      if (canManageAutoAccessOrders || updatingAutoAccept) {
                        return switchEl;
                      }
                      return (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id="auto-accept-permission-tooltip"
                              dir={i18n.dir()}
                            >
                              {autoAcceptOrdersPermissionTooltip}
                            </Tooltip>
                          }
                        >
                          <span
                            className="d-inline-block"
                            style={{ cursor: "not-allowed" }}
                            tabIndex={0}
                            aria-label={autoAcceptOrdersPermissionTooltip}
                          >
                            <span style={{ pointerEvents: "none" }}>{switchEl}</span>
                          </span>
                        </OverlayTrigger>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
          </>
        )}

        <style jsx>{`
          :global(.addon-modal:not(.trackhub-branch-modal) .modal-content) {
            border: none !important;
            border-radius: 15px !important;
            overflow: hidden;
          }

          /* Label alignment for addon modal */
          .addon-label {
            min-width: 120px;
            flex-shrink: 0;
            display: inline-block;
          }

          .addon-modal__row {
            gap: 16px;
          }

          .addon-modal__control {
            flex: 1;
            min-width: 0;
          }

          /* Container background logic */
          .custom-toggle-container > div {
            background-color: #f3f4f6;
            transition: all 0.2s ease-in-out;
            cursor: pointer;
            width: 100%;
            border-radius: 12px !important;
          }

          .custom-toggle-container.is-active > div {
            background-color: #e8f7f0 !important;
          }

          .label-text {
            color: #8c9196;
            font-weight: 500;
            transition: color 0.2s;
          }

          .custom-toggle-container.is-active .label-text {
            color: #00d69f;
            font-weight: 700;
          }

          /* CUSTOM TOGGLE CORE STYLES */
          .custom-switch-root {
            width: 31px;
            height: 17px;
            background-color: #8c8c8c;
            border-radius: 50px;
            position: relative;
            cursor: pointer;
            transition: background-color 0.3s;
            flex-shrink: 0;
          }

          .custom-switch-root.is-checked {
            background-color: var(--active-bg) !important;
          }

          .custom-switch-root.is-disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .custom-switch-thumb {
            width: 13px;
            height: 13px;
            background-color: white;
            border-radius: 50%;
            position: absolute;
            top: 2px;
            left: 2px;
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          }

          .is-checked .custom-switch-thumb {
            transform: translateX(14px);
          }

          :global(html[dir="rtl"]) .custom-switch-thumb {
            left: auto;
            right: 2px;
          }

          :global(html[dir="rtl"]) .is-checked .custom-switch-thumb {
            transform: translateX(-14px);
          }
          
          /* Slug Action Buttons */
          .slug-action-btn {
            width: 32px;
            height: 32px;
            border: none;
            border-radius: 6px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 14px;
            font-weight: bold;
            transition: all 0.2s;
            flex-shrink: 0;
          }
          .slug-save-btn {
            background-color: #28a745 !important;
            color: white !important;
          }
          .slug-save-btn:hover:not(:disabled) {
            background-color: #218838 !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
          }
          .slug-save-btn:disabled {
            background-color: #6c757d !important;
            cursor: not-allowed;
            opacity: 0.5;
          }
          .slug-cancel-btn {
            background-color: #dc3545 !important;
            color: white !important;
          }
          .slug-cancel-btn:hover:not(:disabled) {
            background-color: #c82333 !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
          }
          .slug-cancel-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>
      </Modal>

      {/* Custom CSS for Sync Toggle Switches */}
      <style jsx>{`
        .sync-toggle-switch {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: auto;
          height: 100%;
        }

        .sync-toggle-switch .form-check-input {
          cursor: pointer;
          margin: 0;
          position: relative;
          top: 50%;
          transform: translateY(-50%);
        }

        .sync-toggle-switch .form-check-input:checked {
          background-color: #001f5b;
          border-color: #001f5b;
        }

        .sync-toggle-switch .form-check-input:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 31, 91, 0.25);
        }

        .sync-toggle-switch .form-check-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* More button styling */
        .btn-outline-light.text-dark {
          background-color: #fff !important;
          border-color: #e5e7eb !important;
          color: #000 !important;
          font-size: 0.8rem !important;
        }

        .btn-outline-light.text-dark:hover {
          background-color: #f8f9fa !important;
        }

        /* Available State (Green) */
        .accept-orders-btn {
          background-color: #e3faf4 !important; /* Very light version of #00D69F */
          color: #00d69f !important;
          border: 1px solid #00d69f !important;
          font-size: 0.8rem !important;
        }

        .accept-orders-btn:hover:not(:disabled) {
        }

        /* Unavailable State (Red) */
        .reject-orders-btn {
          background-color: #ffb7b7 !important; /* Very light version of #E84A4A */
          color: #e84a4a !important;
          border: 1px solid #e84a4a !important;
          font-size: 0.8rem !important;
        }

        .reject-orders-btn:hover:not(:disabled) {
        }
      `}</style>

      {renderTimeSlotTables()}

      {/* Auto Accept Confirmation Modal */}
      <ConfirmModal
        show={showAutoAcceptConfirm}
        onHide={() => setShowAutoAcceptConfirm(false)}
        title={autoAcceptModalData.title}
        message={autoAcceptModalData.message}
        action={autoAcceptModalData.action}
      />
    </MDBContainer>
    </div>
  );
};

export default ViewBranchPageV1;
